// @flow
import {
	FacebookAuthProvider,
	GoogleAuthProvider,
	browserLocalPersistence,
	onAuthStateChanged,
	setPersistence,
	signInWithPopup,
} from 'firebase/auth';

import * as api from 'api';
import ApiWorker from 'api/ApiWorker';
import { firebaseAuth } from 'common/firebase';
import { CHECK_AUTH_STATE, SET_AUTH_ERROR, SET_AUTH_BUSY_STATE, LOGIN, LOGOUT } from 'client/constants/actions';

export type LoginParams = {
	type?: 'facebook' | 'google' | 'phone',
	checklistId: string,
};

function parseUserData(user: any) {
	return {
		name: user.displayName || user.email || user.phoneNumber,
		phoneNumber: user.phoneNumber || '',
		email: user.email || '',
		picture: user.photoURL || '',
		uid: user.uid,
	};
}

export function checkAuthState({ checklistId }: LoginParams) {
	return async (dispatch: Dispatch) => {
		try {
			dispatch({ type: CHECK_AUTH_STATE.START });
			// dispatch(logout());
			const result = await new Promise(resolve => onAuthStateChanged(firebaseAuth, user => resolve(!!user)));

			if (result) {
				const user = firebaseAuth.currentUser;
				ApiWorker.token = await user.getIdToken(true);

				const authResult = await api.authUser({ checklistId });
				if (authResult.success) {
					dispatch<UserData>({
						type: CHECK_AUTH_STATE.SUCCESS,
						payload: {
							...parseUserData(user),
							...authResult.data,
						},
					});

					return true;
				}
				throw new Error(authResult.error);
			}

			dispatch(logout());
			dispatch({ type: CHECK_AUTH_STATE.FAIL, payload: 'Unknown Error' });

			return null;
		} catch (e) {
			const msg: string = e.message;

			dispatch({ type: CHECK_AUTH_STATE.FAIL, payload: msg });
			dispatch({ type: SET_AUTH_ERROR, payload: msg });
			dispatch(logout());
		}

		return null;
	};
}

export function login({ type, checklistId }: LoginParams) {
	return async (dispatch: Dispatch) => {
		dispatch({ type: LOGIN.START, payload: type });
		dispatch({ type: SET_AUTH_ERROR, payload: '' });

		try {
			switch (type) {
				case 'facebook': {
					const provider = new FacebookAuthProvider();
					await setPersistence(firebaseAuth, browserLocalPersistence).then(async () => {
						await signInWithPopup(firebaseAuth, provider);
					});
					break;
				}

				case 'google': {
					const provider = new GoogleAuthProvider();
					await firebaseAuth.setPersistence(browserLocalPersistence).then(async () => {
						await signInWithPopup(firebaseAuth, provider);
					});
					break;
				}

				case 'phone':
					// already signed in
					break;

				default:
					break;
			}

			const user = firebaseAuth.currentUser;
			ApiWorker.token = await user.getIdToken(true);

			const authResult = await api.authUser({ checklistId });

			if (authResult.success) {
				dispatch<UserData>({
					type: LOGIN.SUCCESS,
					payload: {
						...parseUserData(user),
						...authResult.data,
					},
				});

				return true;
			}

			dispatch(logout());

			throw new Error(authResult.error);
		} catch (e) {
			const msg: string = e.message;

			dispatch({ type: LOGIN.FAIL, payload: msg });
			dispatch({ type: SET_AUTH_ERROR, payload: msg });
		}

		return null;
	};
}

export function setAuthError(msg: string) {
	return { type: SET_AUTH_ERROR, payload: msg };
}

export function setAuthBusyState(value: string) {
	return { type: SET_AUTH_BUSY_STATE, payload: value };
}

export function logout() {
	return async (dispatch: Dispatch) => {
		dispatch({ type: LOGOUT.START });
		firebaseAuth
			.signOut()
			.then(() => {
				dispatch({ type: LOGOUT.SUCCESS });
			})
			.catch(() => {
				dispatch({ type: LOGOUT.FAIL });
			});
	};
}
